import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
import { dayPartCodeSchema, presetListShowcaseSchema, } from './preset-showcase.js';
import { camelotKeySchema, libraryTypeEnum, tierAccessEnum } from './track.js';
/**
 * Preset status lifecycle — matches DB CHECK on `content.presets.status`
 * (admin-arch §2.4 + decisions §27 selector flow):
 *   draft → in_review (selector-only) → active → archived.
 *
 * Wave 2.17 BE-2 update (TASK-059): the prior export was `presetStatusEnum`
 * with values ['draft','active','archived'] — superseded to match the
 * DB CHECK constraint installed by migration 0026 which adds 'in_review'.
 * `presetStatusEnum` retained as deprecated alias.
 */
export const presetStatusSchema = z.enum([
    'draft',
    'in_review',
    'active',
    'archived',
    'rejected',
    'changes_requested',
]);
/** @deprecated Use `presetStatusSchema`. */
export const presetStatusEnum = presetStatusSchema;
/**
 * Preset author type — discriminates how `author_id` is resolved
 * (admin-arch §2.4 lines 770-771):
 *   curator  → internal.users.id (arc admin)
 *   selector → internal.users.id (role=selector)
 *   client   → customers.accounts.id (custom preset of a tenant)
 *
 * `account_id` denormalized on `content.presets` is populated only when
 * `author_type='client'` (enforced by `presets_account_id_consistency_check`
 * CHECK in migration 0026).
 */
export const presetAuthorTypeSchema = z.enum([
    'curator',
    'selector',
    'client',
]);
/**
 * Filter clause attached to a preset version (admin-arch content.preset_versions
 * `filter_rules`). Each field is independently optional and intersected by AND.
 *
 * Cross-field invariants enforced at the schema boundary (Wave 2.66
 * TASK-060-followup): `bpm_min <= bpm_max`, `length_min_sec <=
 * length_max_sec`, `energy_range.min <= energy_range.max`. Service layer
 * continues to enforce any business rules that depend on DB state.
 */
export const presetFilterSchema = z
    .object({
    include_tags: z.array(uuidSchema).max(50).optional(),
    // BUG-155 / BUG-240 (PRD §4.3 «тумблер любой/все») — combinator for
    // include_tags. Absent or `'any'` means FACETED: selected tags OR within
    // one tag category and AND across categories, matching the catalog's
    // `tags_any` contract (PRD §4.3). `'all'` is the escape hatch — the track
    // must carry EVERY selected tag («трек одновременно jazz И blues»).
    include_tags_mode: z.enum(['any', 'all']).optional(),
    exclude_tags: z.array(uuidSchema).max(50).optional(),
    bpm_min: z.number().positive().max(500).optional(),
    bpm_max: z.number().positive().max(500).optional(),
    camelot_keys: z.array(camelotKeySchema).max(24).optional(),
    vocal_languages: z.array(z.string().max(20)).max(20).optional(),
    anti_artist: z.boolean().optional(),
    /**
     * @deprecated BUG-240 — dead duplicate channel with no writer left. Kept
     * so stored `filter_rules` still validate; the BE resolves it to the
     * energy tags whose slot falls in [min, max] and folds them into the same
     * category facet as any energy tag in `include_tags`. Select energy tags
     * through `include_tags` instead.
     */
    energy_range: z
        .object({
        min: z.number().int().min(1).max(10),
        max: z.number().int().min(1).max(10),
    })
        .optional(),
    /**
     * @deprecated BUG-240 — dead duplicate channel with no writer left. Kept
     * so stored `filter_rules` still validate; the BE folds these ids into the
     * same faceted grouping as `include_tags` (always OR, never affected by
     * `include_tags_mode`). Select mood tags through `include_tags` instead.
     */
    mood: z.array(uuidSchema).max(20).optional(),
    length_min_sec: z.number().int().nonnegative().max(3600).optional(),
    length_max_sec: z.number().int().nonnegative().max(3600).optional(),
})
    .strict()
    .refine((v) => v.bpm_min === undefined ||
    v.bpm_max === undefined ||
    v.bpm_min <= v.bpm_max, {
    message: 'bpm_min must be less than or equal to bpm_max',
    path: ['bpm_min'],
})
    .refine((v) => v.length_min_sec === undefined ||
    v.length_max_sec === undefined ||
    v.length_min_sec <= v.length_max_sec, {
    message: 'length_min_sec must be less than or equal to length_max_sec',
    path: ['length_min_sec'],
})
    .refine((v) => v.energy_range === undefined || v.energy_range.min <= v.energy_range.max, {
    message: 'energy_range.min must be less than or equal to energy_range.max',
    path: ['energy_range'],
});
/**
 * Default crossfade length (seconds) per PRD §4.6.3 rule 8. Applied by
 * readers when a legacy `playback_settings` row predates the field.
 */
export const DEFAULT_CROSSFADE_SECONDS = 3;
/**
 * Rotation rules (admin-arch content.preset_versions `playback_settings` —
 * see also PRD §4.6.3). Cross-field validation (e.g. tier=Lite must keep
 * `key_aware=false`) is enforced at the service layer, not here.
 *
 * `key_aware` defaults to `true`: the rotation scorer applied Camelot
 * weighting unconditionally until 2026-07, so "on" preserves the audible
 * behaviour of every pre-existing preset (migration 0096 flips stored rows).
 *
 * `crossfade_seconds` (PRD §4.6.3 rule 8, 0–12 s) is optional on the wire —
 * absent means {@link DEFAULT_CROSSFADE_SECONDS}; rows minted before the
 * field exist without it.
 */
export const presetRulesSchema = z
    .object({
    key_aware: z.boolean().default(true),
    crossfade_seconds: z.number().int().min(0).max(12).optional(),
    max_bpm_diff: z.number().int().positive().max(60).optional(),
    mood_smoothing: z.boolean().default(false),
    anti_artist_repeat_minutes: z
        .number()
        .int()
        .nonnegative()
        .max(720)
        .default(15),
    min_track_repeat_hours: z
        .number()
        .int()
        .nonnegative()
        .max(720)
        .default(84),
    blocked_intervals: z
        .array(z.object({
        from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    }))
        .optional(),
})
    .strict();
/**
 * Preset version row — wire shape of `content.preset_versions`.
 *
 * `track_count_at_publish` is the persisted catalog-match snapshot taken
 * the moment the version is minted by the publish flow (Wave 2.66 BE-1
 * TASK-060-followup, migration 0068). NULL on drafts and on rows minted
 * before that wave; an integer for every row produced by the new publish
 * path. The preset card UI surfaces it as the "matches N tracks" badge.
 */
export const presetVersionSchema = z
    .object({
    id: uuidSchema,
    preset_id: uuidSchema,
    version_number: z.number().int().positive(),
    filter_rules: presetFilterSchema,
    playback_settings: presetRulesSchema,
    created_by: uuidSchema.nullable(),
    notes: z.string().max(2000).nullable(),
    track_count_at_publish: z.number().int().nonnegative().nullable(),
    created_at: isoDateTimeSchema,
})
    .strict();
/**
 * Preset card / list row — wire shape of `content.presets`.
 * `account_id` is null for curator/selector presets, populated for client
 * presets (see DB CHECK `presets_account_id_consistency_check`).
 */
export const presetSchema = z
    .object({
    id: uuidSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    internal_notes: z.string().max(4000).nullable(),
    cover_s3_key: z.string().max(500).nullable(),
    author_type: presetAuthorTypeSchema,
    author_id: uuidSchema.nullable(),
    account_id: uuidSchema.nullable(),
    library: libraryTypeEnum,
    tier: tierAccessEnum,
    status: presetStatusSchema,
    current_version_id: uuidSchema.nullable(),
    archived_at: isoDateTimeSchema.nullable(),
    // Moderation surface — Wave 2.63 BE-2 (TASK-064). All four columns are
    // null until the first selector submit / admin moderation action.
    moderation_note: z.string().max(2000).nullable(),
    moderated_by_user_id: uuidSchema.nullable(),
    moderated_at: isoDateTimeSchema.nullable(),
    submitted_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
})
    .strict();
/**
 * Preset exclude row — wire shape of `content.preset_excludes`.
 */
export const presetExcludeSchema = z
    .object({
    preset_id: uuidSchema,
    track_id: uuidSchema,
    reason: z.string().max(500).nullable(),
    excluded_by: uuidSchema.nullable(),
    excluded_at: isoDateTimeSchema,
})
    .strict();
/**
 * Preset priority track row — wire shape of `content.preset_priority_tracks`.
 * `weight_multiplier` ∈ {1.20, 1.50, 2.00} (DB CHECK constraint).
 */
export const presetPriorityWeightSchema = z
    .number()
    .refine((v) => v === 1.2 || v === 1.5 || v === 2, {
    message: 'weight_multiplier must be 1.2, 1.5, or 2.0',
});
export const presetPriorityTrackSchema = z
    .object({
    preset_id: uuidSchema,
    track_id: uuidSchema,
    weight_multiplier: presetPriorityWeightSchema,
    set_by: uuidSchema.nullable(),
    set_at: isoDateTimeSchema,
})
    .strict();
/**
 * Mood compatibility row — wire shape of `internal.mood_compat`.
 * Symmetric graph: both (A,B,w) and (B,A,w) are stored (see decisions §35).
 */
export const moodCompatSchema = z
    .object({
    mood_a_id: uuidSchema,
    mood_b_id: uuidSchema,
    weight: z.number().min(0).max(2),
})
    .strict();
/**
 * Wave 2.18 BE-1 — CRUD wire schemas for `/admin/presets*`.
 *
 * Author and account are immutable after creation. LIBRARY AND TIER ARE NOT,
 * since CEO 2026-09-01: a preset created in the free library could never be
 * shown to a venue (the player reads curated only) and there was no way to move
 * it — the curator had to recreate the preset. The admin may now change both.
 * Filter / playback edits still go through a separate version-creating endpoint
 * that lives outside this wave's scope.
 *
 * `presetAuthorTypeSchema` values: `curator | selector | client`. When
 * `author_type='client'`, `account_id` is required (DB CHECK
 * `presets_account_id_consistency_check`); otherwise it MUST be omitted.
 */
export const presetCreateSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    internal_notes: z.string().max(4000).optional(),
    cover_s3_key: z.string().max(500).optional(),
    author_type: presetAuthorTypeSchema,
    author_id: uuidSchema.optional(),
    account_id: uuidSchema.optional(),
    library: libraryTypeEnum,
    tier: tierAccessEnum,
    filter: presetFilterSchema.default({}),
    rules: presetRulesSchema.default({
        key_aware: false,
        mood_smoothing: false,
        anti_artist_repeat_minutes: 15,
        min_track_repeat_hours: 84,
    }),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((v) => (v.author_type === 'client' && v.account_id !== undefined) ||
    (v.author_type !== 'client' && v.account_id === undefined), {
    message: "account_id is required when author_type='client' and must be omitted otherwise",
    path: ['account_id'],
});
export const presetUpdateSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    internal_notes: z.string().max(4000).nullable().optional(),
    cover_s3_key: z.string().max(500).nullable().optional(),
    /**
     * Which library the preset belongs to. Moving OUT of `curated` takes the
     * preset off every venue's «Подборки» at once — the player shows curated
     * only — so the admin UI says so before it saves.
     */
    library: libraryTypeEnum.optional(),
    /** Lowest tariff that may see it. Widens or narrows the audience. */
    tier: tierAccessEnum.optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, {
    message: 'At least one field must be provided',
});
export const presetPublishRequestSchema = z
    .object({
    notes: z.string().max(2000).optional(),
})
    .strict();
export const presetArchiveRequestSchema = z
    .object({
    force: z.boolean().optional().default(false),
})
    .strict();
/**
 * Body for `POST /admin/presets/:id/unarchive` (BUG-377): the return from the
 * archive takes no options, so the body is empty — strict, so a stray `force`
 * is refused rather than silently ignored. The response is
 * `presetPatchResponseSchema`, like archive's.
 */
export const presetUnarchiveRequestSchema = z.object({}).strict();
export const presetDuplicateRequestSchema = z
    .object({
    new_name: z.string().min(1).max(200).optional(),
})
    .strict();
/**
 * Body for `DELETE /admin/presets/:id` (BUG-156). `force=true` overrides the
 * usage check (never for system tier-default presets). Carried in the
 * request BODY — not a query param — so the global `@Audit` interceptor
 * (which snapshots `req.body`) records the force-bypass of a destructive
 * guard in the audit log. Mirrors `presetArchiveRequestSchema`.
 */
export const presetDeleteRequestSchema = z
    .object({
    force: z.boolean().optional().default(false),
})
    .strict();
/**
 * Structured `details` payload of the 409 `PRESET_IN_USE` envelope returned
 * by archive / delete (BUG-156). `venues` is a sample of up to 5 referencing
 * venues so the FE can show WHERE the preset is playing; counts carry the
 * full magnitude.
 */
/**
 * `category_template` (venue-category-discovery I-11, PRD §13) is the third
 * usage source. Without it a curator archives a preset and empties a whole
 * category's template with no warning — the BUG-192 class. `force` semantics
 * are unchanged.
 */
export const presetUsageRefSourceSchema = z.enum([
    'schedule',
    'override',
    'category_template',
]);
/**
 * One entry of the sample list. For `schedule` / `override` the pair is a
 * VENUE; for `category_template` it is the venue CATEGORY whose template holds
 * the preset — a template row has no venue at all, and naming the category is
 * the only answer to «где именно» that means anything to the curator. The field
 * names stay as they are because they are the FE's render + key contract; the
 * `source` discriminator is what says which entity the pair describes.
 */
export const presetInUseVenueSchema = z
    .object({
    venue_id: uuidSchema,
    venue_name: z.string(),
    source: presetUsageRefSourceSchema,
})
    .strict();
export const presetInUseDetailsSchema = z
    .object({
    schedule_entries_count: z.number().int().nonnegative(),
    active_overrides_count: z.number().int().nonnegative(),
    /**
     * Positions in `internal.venue_format_schedule_template_items` (I-11).
     * A separate count rather than a fold into `schedule_entries_count`: the
     * two are fixed by different people on different screens — a schedule slot
     * by the venue, a template position by the curator in the category editor.
     */
    category_template_count: z.number().int().nonnegative(),
    venues: z.array(presetInUseVenueSchema).max(5),
})
    .strict();
/**
 * Body for `POST /admin/presets/:id/excludes` — bulk add. Cap at 1000 to
 * match the architecture's `preset_excludes` soft batch budget; larger lifts
 * should split into multiple requests.
 */
export const presetExcludesAddSchema = z
    .object({
    track_ids: z.array(uuidSchema).min(1).max(1000),
    reason: z.string().max(500).optional(),
})
    .strict();
/**
 * Body for `POST /admin/presets/:id/priority-tracks` — pin a single track
 * with one of the three closed weights `{1.20, 1.50, 2.00}` enforced by the
 * DB CHECK constraint on `preset_priority_tracks.weight_multiplier`.
 */
export const presetPriorityTrackAddSchema = z
    .object({
    track_id: uuidSchema,
    weight_multiplier: presetPriorityWeightSchema,
})
    .strict();
/**
 * Light list-row projection (no version blob, no priority/exclude lists).
 * The card endpoint returns the heavy `presetCardResponseSchema`.
 */
export const presetListItemSchema = z
    .object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    cover_s3_key: z.string().nullable(),
    author_type: presetAuthorTypeSchema,
    author_id: uuidSchema.nullable(),
    author_name: z.string().nullable(),
    account_id: uuidSchema.nullable(),
    library: libraryTypeEnum,
    tier: tierAccessEnum,
    status: presetStatusSchema,
    current_version_id: uuidSchema.nullable(),
    archived_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    excludes_count: z.number().int().nonnegative(),
    priority_tracks_count: z.number().int().nonnegative(),
    /**
     * The «Витрина» column (design §14.2, TASK-622). Populated by
     * `GET /admin/presets`; ABSENT on the selector cabinet list, which has no
     * such column. Optional rather than nullable for that reason: «мы не
     * смотрели» and «пусто» are different answers, and the column renders only
     * the second one.
     */
    showcase: presetListShowcaseSchema.optional(),
})
    .strict();
/**
 * `author_id` (Wave 2.65 BE follow-up, TASK-078a3-followup-presets) — narrows
 * the list to a single author. Combine with `author_type` to disambiguate
 * `internal.users.id` (curator | selector) vs `customers.accounts.id` (client).
 * For tenant client presets `account_id` is the canonical scoping handle;
 * `author_id` is the user-level scope used by the selector card "Presets" tab.
 * RLS already filters selector-visible rows; the filter is redundant-but-
 * harmless for admin readers and required for the selector card UI.
 */
export const presetListFilterSchema = z.object({
    status: presetStatusSchema.optional(),
    author_type: presetAuthorTypeSchema.optional(),
    author_id: uuidSchema.optional(),
    library: libraryTypeEnum.optional(),
    tier: tierAccessEnum.optional(),
    /**
     * Population of the category-template preset picker (design §19.16, arch
     * §3.5). Expands SERVER-side to `TIER_WINDOW[tariff]`; `tier` above is a
     * SINGLE tier and cannot express a window.
     *
     * THE CLIENT NEVER ENCODES THE WINDOW — that is the whole point. A generic
     * `tier_in` CSV was rejected because a client-supplied window is a
     * client-supplied policy, and that is precisely how a pro preset ends up in a
     * Standard template.
     *
     * Values mirror `venueFormatTemplateTariffSchema` (`venue-format.ts`) and are
     * derived from the same `tierAccessEnum`; importing that const here would
     * close a `preset.ts` ↔ `venue-format.ts` cycle, which in this package is a
     * TDZ crash at import time.
     */
    template_tariff: tierAccessEnum.exclude(['lite']).optional(),
    /**
     * Showcase markup state (PRD §6.3, design §14.1, arch §3.3 / I-5).
     *
     * «Не размечено» = NO row in `content.preset_venue_formats` — the definition
     * design §14.1 fixes, and it deliberately survives D-31: without a venue
     * category a preset cannot reach a client at all, so a preset that has day
     * parts and tags but no category is still unmarked. That is the curator's
     * to-do list, not a defect of the filter.
     */
    showcase: z.enum(['marked', 'unmarked']).optional(),
    /** A confirmed `content.preset_venue_formats` link to this category. */
    venue_format_id: uuidSchema.optional(),
    /**
     * A confirmed `content.preset_tags` link to the `day_part` tag with this
     * CODE. Keyed on the code, never on the tag name — a curator may rename
     * «Утро» and the mapping to the schedule slot must survive (ADR-031's
     * `energy_range` lesson).
     */
    day_part: dayPartCodeSchema.optional(),
    account_id: uuidSchema.optional(),
    q: z.string().min(1).max(255).optional(),
    include_deleted: z.coerce.boolean().optional().default(false),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().max(1024).optional(),
});
export const presetListResponseSchema = z.object({
    items: z.array(presetListItemSchema),
    next_cursor: z.string().nullable(),
});
/**
 * Full card payload — includes current version blob, priority/exclude
 * counts, and a snapshot `track_count_at_publish` from when the current
 * version was published. The frontend renders 7 tabs against this object.
 *
 * Wave 2.18 BE-1 R2 (code-review I-1): preset fields are promoted to the
 * top level so the global `AuditInterceptor` (which extracts `entity_id`
 * from `response.id`) can stamp the right preset id on create / duplicate /
 * publish / archive / patch audit rows. Nested `current_version` + `counts`
 * stay as side blocks.
 */
/**
 * Pending (saved but not yet published) draft block on the preset card —
 * BUG-154. Present when the preset has a `content.preset_drafts` row that
 * was actually edited (`updated_at <> created_at`) and not consumed by a
 * publish yet (`published_at IS NULL`). The filter editor re-opens from this
 * state and the Filter tab shows the «Черновик не опубликован» badge.
 */
export const presetCardLatestDraftSchema = z
    .object({
    id: uuidSchema,
    filter_rules: presetFilterSchema,
    playback_settings: presetRulesSchema,
    updated_at: isoDateTimeSchema,
})
    .strict();
export const presetCardResponseSchema = presetSchema.extend({
    /**
     * TASK-409: renderable cover URL — a short-lived (10 min) presigned GET for
     * `cover_s3_key`. The raw key alone is useless to the UI: preset covers live
     * in the private `S3_BUCKET_ENCODED` bucket, so an `<img src>` must use this
     * signed URL. `null` when the preset has no cover OR when the presigner
     * failed (fail-safe — a cover is decoration, it must never break the card).
     * Mirrors the track card's `cover_presigned_url` policy.
     */
    cover_url: z.string().nullable(),
    current_version: presetVersionSchema.nullable(),
    latest_draft: presetCardLatestDraftSchema.nullable(),
    counts: z.object({
        excludes_count: z.number().int().nonnegative(),
        priority_tracks_count: z.number().int().nonnegative(),
        matching_tracks_count_at_publish: z.number().int().nonnegative().nullable(),
    }),
});
/**
 * Response of `POST /admin/presets/:id/cover` (TASK-408) — multipart upload of
 * a single image (field name `file`) into an EXISTING preset.
 *
 * Carries both handles the card needs after an upload: `cover_s3_key` is the
 * persisted column value, `cover_url` is a ready-to-render presigned GET so
 * the UI can swap the image without re-fetching the card. `cover_url` is
 * nullable purely for the fail-safe presigner path — the upload itself
 * succeeded whenever this response is returned.
 *
 * Server-side limits (rejected at the HTTP boundary):
 *   * MIME ∈ {image/jpeg, image/png, image/webp} → else 415
 *     `PRESET_COVER_MIME_REJECTED`;
 *   * ≤ 5 MB → else 413 `PRESET_COVER_TOO_LARGE`;
 *   * non-empty → else 400 `PRESET_COVER_EMPTY`;
 *   * preset must exist and not be soft-deleted → else 404 `PRESET_NOT_FOUND`.
 *
 * There is deliberately NO delete-cover endpoint: replacing a cover means
 * uploading another one.
 */
export const presetCoverUploadResponseSchema = z
    .object({
    preset_id: uuidSchema,
    cover_s3_key: z.string().max(500),
    cover_url: z.string().nullable(),
})
    .strict();
/**
 * Create / duplicate response — top-level preset fields + nested
 * `current_version` so audit `entity_id` resolves automatically. See
 * `presetCardResponseSchema` doc-block for the rationale.
 */
export const presetCreateResponseSchema = presetSchema.extend({
    current_version: presetVersionSchema,
});
/** Patch / archive response — flat preset row (no nested blocks). */
export const presetPatchResponseSchema = presetSchema;
/** Publish response — flat preset row + version blob + computed match count. */
export const presetPublishResponseSchema = presetSchema.extend({
    version: presetVersionSchema,
    matching_tracks_count: z.number().int().nonnegative(),
});
export const presetVersionListResponseSchema = z.object({
    items: z.array(presetVersionSchema.extend({
        /**
         * Snapshot of "matching tracks at publish-time" — null until the
         * DB column lands (Wave 2.18 BE-1 follow-up: see impl-report
         * `docs/impl/preset-endpoints-backend.md`). Frontend renders a
         * placeholder when null.
         */
        track_count_at_publish: z.number().int().nonnegative().nullable(),
        is_current: z.boolean(),
    })),
});
/**
 * Wave 2.19 BE-1 (TASK-062) — live preset preview endpoints.
 *
 * Two read-only endpoints power the preset editor's "live counter" and
 * "Послушать 5 треков" mini-strip (PRD §4.3, admin-arch §3.4 / §4.6.5(д)):
 *   - `GET /admin/presets/:id/track-count`        → matching count + filter summary.
 *   - `GET /admin/presets/:id/preview-5-tracks`   → up to 5 sampled tracks.
 *
 * Filter summary mirrors the **on-disk** `presetFilterSchema` shape verbatim
 * (`include_tags` / `exclude_tags` / `bpm_min` / `bpm_max` / `mood` / etc.) so
 * the FE consumes the same field names it submits when creating a preset.
 *
 * Filter predicate (consistent with Wave 2.18 BE-1 `publish`): the SQL applies
 * the faceted tag predicate (`include_tags` + the deprecated `mood` /
 * `energy_range` channels — OR within a tag category, AND across categories;
 * `include_tags_mode: 'all'` switches include_tags to a per-tag intersection),
 * `exclude_tags`, bpm range (`track_versions.bpm`), `camelot_keys`,
 * `vocal_languages`, `length_min_sec` / `length_max_sec`, plus
 * `content.preset_excludes`. `anti_artist` is NOT applied — it is a
 * per-session rotation rule, not a catalog predicate.
 */
export const presetFilterSummarySchema = presetFilterSchema;
export const presetTrackCountResponseSchema = z
    .object({
    preset_id: uuidSchema,
    version_id: uuidSchema,
    version_number: z.number().int().positive(),
    matching_count: z.number().int().nonnegative(),
    filter_summary: presetFilterSummarySchema,
    excludes_count: z.number().int().nonnegative(),
    priorities_count: z.number().int().nonnegative(),
    computed_at: isoDateTimeSchema,
})
    .strict();
/**
 * Preview track item — single row inside `preview_tracks[]`. Mirrors the
 * fields the mini-strip renders (cover, BPM, key, length) plus the
 * `priority_weight` annotation used by the FE to draw the "приоритет"
 * badge. `hls_master_playlist_key` is forwarded as-is so the FE can ask
 * `/admin/tracks/:id/playback.m3u8` to mint a signed admin-preview manifest
 * (ADR-008); the DEK delivery flow is handled by the existing
 * `/api/v1/admin/tracks/:id/preview-key/:version` endpoint.
 */
export const presetPreviewTrackItemSchema = z
    .object({
    id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    bpm: z.number().int().nullable(),
    camelot_key: z.string().nullable(),
    duration_seconds: z.number().int().nullable(),
    library: libraryTypeEnum,
    tier: tierAccessEnum,
    cover_s3_key: z.string().nullable(),
    hls_master_playlist_key: z.string().nullable(),
    has_explicit_lyrics: z.enum(['yes', 'no', 'unknown']),
    priority_weight: presetPriorityWeightSchema.nullable(),
})
    .strict();
export const presetPreviewTracksResponseSchema = z
    .object({
    preset_id: uuidSchema,
    version_id: uuidSchema,
    version_number: z.number().int().positive(),
    preview_tracks: z.array(presetPreviewTrackItemSchema).max(5),
    total_matching_count: z.number().int().nonnegative(),
    computed_at: isoDateTimeSchema,
})
    .strict();
/**
 * `GET /admin/presets/:id/preview-sequence` — a 5-track listening sequence
 * built by chaining the REAL rotation scoring (Camelot / mood / priority,
 * PRD §331: «играют последовательно с заданным плавным переходом и учётом
 * тональности»). Track N+1 is picked with track N as the scoring context —
 * the same ordering logic the venue player hears, minus venue-specific hard
 * filters (play history, tier window) which have no admin-preview analogue.
 *
 * `crossfade_seconds` echoes the version's playback setting (defaulted) so
 * the admin player can fade exactly like the venue device.
 *
 * The rotation is endless, so «вся последовательность» is served in batches:
 * the client replays the chain by passing the last played track as
 * `current_track_id` (scoring context for the first pick of the new batch)
 * plus `exclude_track_ids` (already-played ids to keep out, mirroring the
 * venue min-track-repeat window). When the exclusions exhaust the pool the
 * service drops them — same spirit as the venue cascade relaxation level 2.
 */
export const presetPreviewSequenceResponseSchema = z
    .object({
    preset_id: uuidSchema,
    version_id: uuidSchema,
    version_number: z.number().int().positive(),
    crossfade_seconds: z.number().int().min(0).max(12),
    key_aware: z.boolean(),
    sequence: z.array(presetPreviewTrackItemSchema).max(20),
    total_matching_count: z.number().int().nonnegative(),
    computed_at: isoDateTimeSchema,
})
    .strict();
/**
 * Query of `GET /admin/presets/:id/preview-sequence`.
 *
 * `count` — batch size (default 5, hard cap 20 per request).
 * `current_track_id` — chain continuation: the track the previous batch
 * ended on; the first pick of this batch is scored against it.
 * `exclude_track_ids` — comma-separated uuids already played this listening
 * session (client keeps the last ≤200); excluded unless that empties the pool.
 */
export const presetPreviewSequenceQuerySchema = z
    .object({
    seed: z.coerce.number().int().min(0).max(2_147_483_647).optional(),
    count: z.coerce.number().int().min(1).max(20).optional(),
    current_track_id: uuidSchema.optional(),
    exclude_track_ids: z
        .string()
        .transform((s) => s.split(',').filter((v) => v.length > 0))
        .pipe(z.array(uuidSchema).max(200))
        .optional(),
})
    .strict();
/**
 * `?seed=<int 0..2^31-1>` — optional deterministic ordering for demos.
 * When absent, the service uses a fresh-random selection inside each
 * priority tier so consecutive calls surface different samples (CEO demo
 * "ещё 5 пожалуйста" UX).
 */
export const presetPreviewQuerySchema = z
    .object({
    seed: z.coerce.number().int().min(0).max(2_147_483_647).optional(),
})
    .strict();
/**
 * Wave 2.57 BE-1 (TASK-282-followup-be-preview-count) — draft-filter live count.
 *
 * `POST /admin/presets/preview-count` accepts an ad-hoc (unsaved) filter body
 * and returns the count of tracks that would match it inside the requested
 * library. Used by the preset editor's «Живой счётчик» (admin-design §6.4 /
 * frontend-review F-03) — including for brand-new presets where no
 * `current_version` exists yet (UID-Story PRE-1 «пустой черновик»).
 *
 * Unlike the existing `GET /admin/presets/:id/track-count` (Wave 2.19 BE-1,
 * TASK-062) which reads the persisted `current_version.filter_rules`, this
 * endpoint takes the draft filter directly from the request body so the FE
 * can debounce calls (300ms) as the user edits.
 *
 * The filter predicate matches `computeMatchingTracksCount` in
 * `presets.service.ts` (status='published' + not deleted + the faceted tag
 * predicate + bpm range + the remaining catalog-evaluable fields).
 * `account_id` is an optional pass-through that the service may use for
 * tenant-scoped checks in a later wave — today the read is library-wide.
 */
export const presetPreviewCountRequestSchema = z
    .object({
    filter: presetFilterSchema,
    library: libraryTypeEnum,
    account_id: uuidSchema.optional(),
})
    .strict();
export const presetPreviewCountResponseSchema = z
    .object({
    matching_count: z.number().int().nonnegative(),
    computed_at: isoDateTimeSchema,
})
    .strict();
/**
 * Wave 2.26 BE-2 (TASK-063) — preset version rollback.
 *
 * `POST /admin/presets/:id/rollback/:version_id` mints a NEW immutable
 * `preset_versions` row whose `filter_rules` + `playback_settings` are
 * copied from the target historical version, then atomically swings
 * `presets.current_version_id` to that new row. The original target
 * version stays untouched in history (immutable new-row pattern from
 * Wave 2.18 BE-1 R2 — see `publish`).
 *
 * Players using the preset switch to the rolled-back version at the next
 * `/next-track` call (not mid-track) — Wave 2.22 BE-1 `PlayerRotationService`
 * re-resolves `current_version_id` on each call, per PRD §4.4 "уже
 * играющий трек доигрывает, следующий — из новой версии".
 *
 * Optional `reason` (max 500 chars) is persisted on the new version row's
 * `notes` column with a `[rollback to v<n>] ` prefix, so the version-list
 * UI can render the audit trail without a separate column.
 */
export const presetRollbackRequestSchema = z
    .object({
    reason: z.string().max(500).optional(),
})
    .strict();
/**
 * Rollback response — flat preset row + new version blob + computed
 * matching-tracks count. Same shape as `presetPublishResponseSchema`
 * (Wave 2.18 BE-1 R2 — preset fields at top level so the global
 * `AuditInterceptor` resolves `entity_id` from `response.id`); the
 * FE's `useRollbackPresetVersionMutation` consumes `PresetPublishResponse`
 * today and will switch to this type once the BE merges.
 */
export const presetRollbackResponseSchema = presetSchema.extend({
    version: presetVersionSchema,
    matching_tracks_count: z.number().int().nonnegative(),
});
/**
 * Wave 2.61 BE-1 (TASK-107c-followup-be) — selector «Мои пресеты».
 *
 * `GET /selector/presets` — list scoped to the calling selector's own presets
 * (`author_type='selector' AND author_id=actor.id`). Reuses the existing
 * `presetListItemSchema` for the row shape and `presetListResponseSchema` for
 * the envelope — selector-visible fields are identical to the admin list
 * (no extra sensitive columns to mask).
 *
 * Wave 2.37 FE (TASK-107c) sends `q` + `status` filters via the same query
 * shape as `/admin/presets`, so we reuse `presetListFilterSchema` too. The
 * selector cannot override `author_type` / `author_id` / `account_id` — those
 * are stripped at the service layer (and would 403 anyway via RLS / explicit
 * scope predicate).
 *
 * `POST /selector/presets/:id/submit-review` — selector transitions one of
 * their own presets from `draft` → `in_review`. Body carries an optional
 * `notes` free-text (max 2000). Response mirrors `presetCardResponse` so the
 * FE can refresh the card after the transition.
 *
 * Wave 2.61 BE-1 R2 (Process A I-1): `notes` is currently **NOT persisted**
 * anywhere — it is not written to `content.preset_versions.notes` (that
 * column is reserved for publish / rollback annotations) and it does not
 * land on the audit_log row (the global `AuditInterceptor` writes only the
 * `presets`-table diff via `diffTable`; the field is not part of that
 * snapshot). The schema keeps the field so the FE wire contract stays
 * stable and a future wave can promote it to a dedicated DB column without
 * a breaking change. Until then, callers should treat `notes` as fire-and-
 * forget context that does not survive the request.
 *
 * Lifecycle reminder (admin-arch §2.4 / decisions §27 + DB CHECK in migration
 * 0026): the next valid status after `in_review` is `active` (admin publish)
 * — there is no `pending_review` value in the DB CHECK. The brief used
 * `pending_review` as a conceptual term; the wire/DB value is `in_review`.
 */
export const selectorPresetSubmitReviewRequestSchema = z
    .object({
    notes: z.string().max(2000).optional(),
})
    .strict();
/**
 * Admin preset moderation — Wave 2.63 BE-2 (TASK-064). Three endpoints
 * under `/admin/presets/:id/{approve,reject,request-changes}`. All three
 * are guarded by `@AdminOrAbove()` and audit-logged via the `@Audit`
 * decorator on the controller.
 *
 *   approve         → status `active` (synonym for the brief's "published")
 *   reject          → status `rejected`        (reason required)
 *   request-changes → status `changes_requested` (comments required)
 *
 * `note`/`reason`/`comments` are persisted into `content.presets.moderation_note`
 * (single text column; the differentiator is the status itself + the audit
 * action). Max length matches the submit-review schema (2000 chars).
 */
export const adminPresetApproveRequestSchema = z
    .object({
    note: z.string().max(2000).optional(),
})
    .strict();
export const adminPresetRejectRequestSchema = z
    .object({
    reason: z.string().min(1).max(2000),
})
    .strict();
export const adminPresetRequestChangesRequestSchema = z
    .object({
    comments: z.string().min(1).max(2000),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.67 BE polish — preset drafts (TASK-066a-followup-be-create-draft-version).
//
// FE precursor: the filter-editor modal needs an editable scratch row to
// persist user-typed filter mutations between sessions without violating
// the `preset_versions_immutable_filter_playback` trigger (migration 0068).
//
// Three endpoints:
//   * POST  /admin/presets/:id/draft-version       — clone current_version
//     filter+playback into a new content.preset_drafts row.
//   * PATCH /admin/preset-drafts/:id               — mutate filter_rules
//     and/or playback_settings.
//   * POST  /admin/presets/:id/preview-count       — ad-hoc count using the
//     preset's preset_excludes subtract (distinct from the existing
//     library-only POST /admin/presets/preview-count).
// ---------------------------------------------------------------------------
/**
 * Wire row of `content.preset_drafts`.
 */
export const presetDraftSchema = z
    .object({
    id: uuidSchema,
    preset_id: uuidSchema,
    filter_rules: presetFilterSchema,
    playback_settings: presetRulesSchema,
    created_by: uuidSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
})
    .strict();
/**
 * Body of `POST /admin/presets/:id/draft-version` — none. The service
 * clones the source from the preset's current_version. We still validate
 * an empty body to reject stray keys (defence-in-depth).
 */
export const presetDraftCreateRequestSchema = z.object({}).strict();
/**
 * Response of `POST /admin/presets/:id/draft-version` — the just-minted
 * draft row, plus a flat `draft_version_id` alias for legacy callers /
 * the brief's wire shape.
 */
export const presetDraftCreateResponseSchema = presetDraftSchema.extend({
    draft_version_id: uuidSchema,
});
/**
 * Body of `PATCH /admin/preset-drafts/:id`. Both `filter_rules` and
 * `playback_settings` are optional but at least one MUST be present.
 * Unknown keys are rejected via `.strict()`.
 */
export const presetDraftPatchRequestSchema = z
    .object({
    filter_rules: presetFilterSchema.optional(),
    playback_settings: presetRulesSchema.optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, {
    message: 'At least one field must be provided',
});
export const presetDraftPatchResponseSchema = presetDraftSchema;
/**
 * Body of `POST /admin/presets/:id/preview-count` — ad-hoc filter applied
 * to the named preset (so `preset_excludes` is honoured). Separate from
 * the existing library-wide `POST /admin/presets/preview-count` because
 * the per-id form gets to drop tracks the admin has explicitly excluded.
 */
export const presetPreviewCountByIdRequestSchema = z
    .object({
    filter_rules: presetFilterSchema,
})
    .strict();
export const presetPreviewCountByIdResponseSchema = z
    .object({
    preset_id: uuidSchema,
    matching_count: z.number().int().nonnegative(),
    excludes_count: z.number().int().nonnegative(),
    computed_at: isoDateTimeSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.67 BE polish — preset tracks list (TASK-066b-followup-be-tracks-list).
//
// `GET /admin/presets/:id/tracks` — paginated list of tracks the preset
// currently matches (filter_rules) PLUS the priority-track manual
// overrides. Columns map 1:1 to the FE preset card "Треки" tab.
// ---------------------------------------------------------------------------
export const presetTracksListQuerySchema = z
    .object({
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
})
    .strict();
/**
 * Per-row shape for `GET /admin/presets/:id/tracks`.
 *
 * `included_via`:
 *   * 'rule'   — the track matches the preset's filter_rules.
 *   * 'manual' — the track is in `preset_priority_tracks` (overrides the
 *                rule predicate; surfaced even when the catalog row would
 *                not otherwise match).
 *
 * `last_played_at` is sourced from `content.play_events` aggregation —
 * NULL when the catalog has never seen the track played in any venue.
 */
export const presetTracksListItemSchema = z
    .object({
    id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    bpm: z.number().int().nullable(),
    camelot_key: z.string().nullable(),
    last_played_at: isoDateTimeSchema.nullable(),
    included_via: z.enum(['rule', 'manual']),
})
    .strict();
export const presetTracksListResponseSchema = z
    .object({
    items: z.array(presetTracksListItemSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.68 BE Goal 4 — preset excludes + priority-tracks list endpoints.
//
// `GET /admin/presets/:id/excludes` — paginated list of `preset_excludes`
// rows with the same offset/limit shape as `/admin/presets/:id/tracks`.
// Surfaces enough track metadata (id, title, artist, bpm, camelot_key,
// library_type) for the FE Excludes tab to render its table without a
// follow-up fetch.
//
// `GET /admin/presets/:id/priority-tracks` — same shape + `weight_multiplier`
// (1.20 / 1.50 / 2.00) sourced from `content.preset_priority_tracks`.
// ---------------------------------------------------------------------------
export const presetExcludesListQuerySchema = z
    .object({
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
})
    .strict();
/**
 * Per-row shape for `GET /admin/presets/:id/excludes`.
 *
 * `track_id` is the canonical FK into `content.tracks`. `added_at` mirrors
 * `preset_excludes.excluded_at` on the wire — the FE column label is
 * "Добавлено в исключения".
 *
 * `added_by_user_id` is the actor that called POST `/excludes`; nullable
 * because the underlying FK is `ON DELETE SET NULL` (a removed admin's
 * historical excludes survive without leaking PII).
 */
export const presetExcludesListItemSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    bpm: z.number().int().nullable(),
    camelot_key: camelotKeySchema.nullable(),
    library_type: libraryTypeEnum,
    added_at: isoDateTimeSchema,
    added_by_user_id: uuidSchema.nullable(),
})
    .strict();
export const presetExcludesListResponseSchema = z
    .object({
    items: z.array(presetExcludesListItemSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
export const presetPriorityListQuerySchema = z
    .object({
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
})
    .strict();
/**
 * Per-row shape for `GET /admin/presets/:id/priority-tracks`.
 *
 * `weight_multiplier` is the canonical 1.20 / 1.50 / 2.00 enum
 * (`presetPriorityWeightSchema`) — same as the POST-write surface.
 *
 * `set_at` / `set_by_user_id` map to `preset_priority_tracks.set_at` /
 * `set_by` on the DB. There is no `is_pin_only` column on this table —
 * the FE tab does not need to differentiate pin-only from weighted
 * (all priority-tracks rows are explicit pins by definition).
 */
export const presetPriorityListItemSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    bpm: z.number().int().nullable(),
    camelot_key: camelotKeySchema.nullable(),
    library_type: libraryTypeEnum,
    weight_multiplier: presetPriorityWeightSchema,
    set_at: isoDateTimeSchema,
    set_by_user_id: uuidSchema.nullable(),
})
    .strict();
export const presetPriorityListResponseSchema = z
    .object({
    items: z.array(presetPriorityListItemSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.69 BE — preset usage list (TASK-066c-followup-usage).
//
// `GET /admin/presets/:id/usage?limit&offset` — paginated list of venues that
// reference this preset via `customers.schedule_entries.preset_id_placeholder`
// (the historical column name; the real FK to `content.presets(id)` was added
// by migration 0070). Each row aggregates the number of active schedule slots
// for the venue + the most recent `content.play_events.started_at` where the
// preset was bound at play time (`preset_id_at_play`).
//
// Wire shape mirrors the offset-paginated envelope already used by the
// excludes / priority / tracks list endpoints (`{ items, total, limit, offset }`).
// ---------------------------------------------------------------------------
export const presetUsageListQuerySchema = z
    .object({
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
})
    .strict();
/**
 * Per-row shape for `GET /admin/presets/:id/usage`.
 *
 * `active_slots_count` — number of `customers.schedule_entries` rows for the
 * venue that reference this preset (one row per `(day_of_week, start_local_time)`
 * combination; the same preset can occupy multiple slots within a venue).
 *
 * `last_played_at` — `MAX(content.play_events.started_at)` filtered by
 * `venue_id` + `preset_id_at_play = :preset_id`. NULL when the preset has been
 * scheduled but never actually played (e.g. just-pinned preset, or a venue with
 * no players online during the schedule window).
 *
 * `account_name` is a server-resolved label — `COALESCE(company_name,
 * contact_name, contact_email)` so the FE always has something printable.
 */
export const presetUsageListItemSchema = z
    .object({
    venue_id: uuidSchema,
    venue_name: z.string(),
    account_id: uuidSchema,
    account_name: z.string(),
    active_slots_count: z.number().int().nonnegative(),
    last_played_at: isoDateTimeSchema.nullable(),
})
    .strict();
export const presetUsageListResponseSchema = z
    .object({
    items: z.array(presetUsageListItemSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=preset.js.map